import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

const BarsStack = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 100%;
`;

const BarBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  flex: 1 1 0;
  min-width: 0;
`;

const BarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.25rem;
  gap: 0.5rem;
`;

const BarLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => props.theme.primaryText};
`;

const BarValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) => props.theme.primaryText};
  flex-shrink: 0;
`;

const BarTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${(props) => props.theme.border || '#374151'};
  overflow: hidden;
  display: flex;
  flex-direction: row;
`;

const BarSegment = styled.div`
  height: 100%;
  min-width: ${(props) => (props.$ratio > 0 ? '2px' : '0')};
  flex: ${(props) => props.$ratio};
  background: ${(props) => props.$color};
  transition: flex 0.3s ease;
`;

/**
 * Horizontal progress bars for Featured Picks and Lineups Posted.
 * Replaces circular dials to save vertical space, especially on mobile.
 * Same segment colors: green (hit/posted), gray (pending), black (final/cancelled).
 */
function FeaturedAndLineupBars({ predictions = [], featured = [], featuredHitThreshold = 1 }) {
  const { theme } = useTheme();

  // Lineups Posted: green = posted, gray = not yet, black = cancelled/postponed
  const totalLineup = predictions?.length ?? 0;
  let lineupGreen = 0,
    lineupGray = 0,
    lineupBlack = 0;
  if (totalLineup > 0) {
    predictions.forEach((pick) => {
      const isLineupPosted =
        pick.lineupsPosted === true || pick.lineupsPosted === 'true' || pick.lineupsPosted === 'True';
      const isCancelled =
        pick.gameStatus === 'Cancelled' ||
        pick.gameStatus === 'Postponed' ||
        pick.gameStatus === 'Suspended';
      if (isLineupPosted) lineupGreen++;
      else if (isCancelled) lineupBlack++;
      else lineupGray++;
    });
  }
  const lineupPercent = totalLineup === 0 ? 0 : Math.round((lineupGreen / totalLineup) * 100);
  const lineupSegments = [];
  if (totalLineup > 0) {
    if (lineupGreen > 0) lineupSegments.push({ ratio: lineupGreen / totalLineup, color: '#38a169' });
    if (lineupGray > 0) lineupSegments.push({ ratio: lineupGray / totalLineup, color: '#718096' });
    if (lineupBlack > 0) lineupSegments.push({ ratio: lineupBlack / totalLineup, color: '#2d3748' });
  }

  // Featured Picks: green = hit (actualValue >= threshold), black = final miss, gray = pending
  const totalFeatured = featured.length;
  let featGreen = 0,
    featBlack = 0,
    featGray = 0;
  featured.forEach((pick) => {
    if (Number(pick.actualValue) >= featuredHitThreshold) featGreen++;
    else if (pick.gameStatus === 'Final' || pick.gameStatus === 'Game Over') featBlack++;
    else featGray++;
  });
  const featSegments = [];
  if (totalFeatured > 0) {
    if (featGreen > 0) featSegments.push({ ratio: featGreen / totalFeatured, color: '#38a169' });
    if (featBlack > 0) featSegments.push({ ratio: featBlack / totalFeatured, color: '#2d3748' });
    if (featGray > 0) featSegments.push({ ratio: featGray / totalFeatured, color: '#718096' });
  }

  return (
    <BarsStack>
      <BarBlock>
        <BarHeader>
          <BarLabel theme={theme}>Lineups Posted</BarLabel>
          <BarValue theme={theme}>{totalLineup === 0 ? '0%' : `${lineupPercent}%`}</BarValue>
        </BarHeader>
        <BarTrack theme={theme}>
          {totalLineup === 0 ? (
            <BarSegment $ratio={1} $color="#4b5563" />
          ) : (
            lineupSegments.map((seg, i) => (
              <BarSegment key={i} $ratio={seg.ratio} $color={seg.color} />
            ))
          )}
        </BarTrack>
      </BarBlock>
      <BarBlock>
        <BarHeader>
          <BarLabel theme={theme}>Featured Picks</BarLabel>
          <BarValue theme={theme}>{totalFeatured}</BarValue>
        </BarHeader>
        <BarTrack theme={theme}>
          {totalFeatured === 0 ? (
            <BarSegment $ratio={1} $color="#4b5563" />
          ) : (
            featSegments.map((seg, i) => (
              <BarSegment key={i} $ratio={seg.ratio} $color={seg.color} />
            ))
          )}
        </BarTrack>
      </BarBlock>
    </BarsStack>
  );
}

export default FeaturedAndLineupBars;
